import { AppBar, Badge, IconButton, Toolbar } from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { LogoText } from '@farmshare/ui-components';

import { NotificationBell } from './notification-bell';

interface MobileTopBarProps {
  onOpenDrawer: () => void;
  height: number;
  /**
   * Outstanding items behind the menu. On a phone the whole nav is hidden
   * inside this button, so without a badge there is no way to know anything
   * is waiting without opening it — which is the opposite of "they open the
   * app and can see what's blocked".
   */
  badgeCount?: number;
}

export function MobileTopBar({
  onOpenDrawer,
  height,
  badgeCount = 0,
}: MobileTopBarProps) {
  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        bgcolor: 'primary.main',
        color: 'text.primary',
        height,
        display: { xs: 'flex', md: 'none' },
        '@media print': { display: 'none' },
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          minHeight: `${height}px !important`,
          height,
          px: 2,
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
        }}
      >
        <IconButton
          onClick={onOpenDrawer}
          aria-label={
            badgeCount > 0
              ? `Open menu — ${badgeCount} needing attention`
              : 'Open menu'
          }
          // Past the notch in landscape; a plain 8px in portrait.
          sx={{
            position: 'absolute',
            left: 'calc(8px + env(safe-area-inset-left, 0px))',
            color: 'background.default',
          }}
        >
          <Badge
            badgeContent={badgeCount}
            // Always red. The menu badge is the app's one signal that
            // something is waiting, and amber on a green bar reads as
            // decoration rather than as a count.
            color="error"
            invisible={!badgeCount}
            overlap="circular"
          >
            <MenuIcon />
          </Badge>
        </IconButton>
        <LogoText
          sx={{
            height: 30,
            minWidth: 120,
            maxWidth: 120,
            color: 'background.default',
          }}
        />
        <NotificationBell
          sx={{
            position: 'absolute',
            right: 'calc(8px + env(safe-area-inset-right, 0px))',
            color: 'background.default',
          }}
        />
      </Toolbar>
    </AppBar>
  );
}
